import { onBeforeUnmount, onMounted, watch, type Ref } from 'vue';
import { usePageContext } from 'vike-vue/usePageContext';
import { isFullScreen, onFullScreenChange } from '../utils/fullScreen';
import { isFramed } from '../utils/isFramed';
import { measureHeight } from '../utils/measureHeight';
import { createParentFrameMessenger } from '../utils/parentFrameMessenger';

// Steps reached by a full-page navigation mid-flow rather than by client-side
// routing: Stripe's `requires_capture` branch and the common 3DS/redirect
// payment return land on `/finalize`. On those the layout is created fresh, so
// the urlPathname watch never fires -- we emit scroll-to-top on mount instead.
// Deliberately excludes the initial tickets/`checkout` landing, so a
// below-the-fold embed a host has scrolled down to reach is not yanked to the
// top of the host document on first load. (One minor gap: a 3DS return for a
// still-`draft` order goes back to `/checkout` rather than `/finalize`, so it
// gets no mount scroll-to-top -- an accepted trade-off to keep the below-the-fold
// guarantee on `/checkout`, which can also be a genuine first landing.)
const isPaymentReturnPath = (pathname: string): boolean => /\/(finalize|confirmation)(\/|$)/.test(pathname);

// Marker set on <html> while framed. checkout.css keys off it to neutralise
// viewport-height utilities (min-h-screen / h-screen / the desktop cart's 100vh):
// inside an auto-height frame those equal the height we just reported, so stacked
// with fixed chrome each report grows past the last -- an unbounded resize loop.
// It lives on <html> (outside Vue's hydrated tree) so toggling it never triggers
// a hydration mismatch on the ssr:true tickets page.
const FRAMED_MARKER_CLASS = 'vp-checkout-framed';

/**
 * While framed, reports the app-root content height to the embedding page on
 * every change (via a ResizeObserver, not a timer) and requests scroll-to-top on
 * step change. A no-op when checkout owns the page. See
 * packages/checkout/doc/claude/iframe-embedding.md for the message contract.
 */
export const useParentFrameSync = (root: Ref<HTMLElement | null>): void => {
  // Attaching a ResizeObserver and a route watch only makes sense inside a
  // frame; the messenger separately enforces the "nothing posted top-level"
  // guarantee, so both layers agree.
  if (!isFramed()) {
    return;
  }

  // Set synchronously in setup (before first paint and before the observer is
  // attached) so viewport heights are already neutralised when the first height
  // is measured -- otherwise the very first report is a full-viewport value that
  // seeds the loop.
  document.documentElement.classList.add(FRAMED_MARKER_CLASS);

  const messenger = createParentFrameMessenger();
  const pageContext = usePageContext();
  let observer: ResizeObserver | undefined;
  let stopFullScreenListener: (() => void) | undefined;

  onMounted(() => {
    const element = root.value;
    if (!element) {
      return;
    }

    // The initial observe callback fires synchronously with the mounted size,
    // so the host gets a first height without any extra plumbing.
    observer = new ResizeObserver(entries => {
      if (isFullScreen()) {
        return;
      }

      messenger.postResize(measureHeight(element, entries[0]));
    });
    observer.observe(element);

    // A full-screen chart leaves the flow: posting its height would shrink the host's frame.
    stopFullScreenListener = onFullScreenChange(() => {
      if (isFullScreen()) {
        return;
      }

      messenger.postResize(measureHeight(element));
    });

    if (isPaymentReturnPath(pageContext.urlPathname)) {
      messenger.postScrollToTop();
    }
  });

  watch(
    () => pageContext.urlPathname,
    () => messenger.postScrollToTop(),
  );

  onBeforeUnmount(() => {
    observer?.disconnect();
    stopFullScreenListener?.();
    document.documentElement.classList.remove(FRAMED_MARKER_CLASS);
  });
};
